import {NgIf} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation, DOCUMENT, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {FuseConfig, FuseConfigService} from '@fuse/services/config';
import {FuseLoadingService} from '@fuse/services/loading';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {FusePlatformService} from '@fuse/services/platform';
import {FUSE_VERSION} from '@fuse/version';
import {combineLatest, filter, map, Subject, takeUntil} from 'rxjs';
import {EmptyLayoutComponent} from './layouts/empty/empty.component';
import {CenteredLayoutComponent} from './layouts/horizontal/centered/centered.component';
import {EnterpriseLayoutComponent} from './layouts/horizontal/enterprise/enterprise.component';
import {MaterialLayoutComponent} from './layouts/horizontal/material/material.component';
import {ModernLayoutComponent} from './layouts/horizontal/modern/modern.component';
import {ClassicLayoutComponent} from './layouts/vertical/classic/classic.component';
import {ClassyLayoutComponent} from './layouts/vertical/classy/classy.component';
import {CompactLayoutComponent} from './layouts/vertical/compact/compact.component';
import {DenseLayoutComponent} from './layouts/vertical/dense/dense.component';
import {FuturisticLayoutComponent} from './layouts/vertical/futuristic/futuristic.component';
import {ThinLayoutComponent} from './layouts/vertical/thin/thin.component';
import {UserPreferenceService} from "../services/user-preference.service";

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgIf, EmptyLayoutComponent, CenteredLayoutComponent, EnterpriseLayoutComponent, MaterialLayoutComponent, ModernLayoutComponent, ClassicLayoutComponent, ClassyLayoutComponent, CompactLayoutComponent, DenseLayoutComponent, FuturisticLayoutComponent, ThinLayoutComponent]
})
export class LayoutComponent implements OnInit, OnDestroy {
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private _userPreferenceService: UserPreferenceService,
        private _fuseLoadingService: FuseLoadingService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the theme and scheme based on the configuration
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)', '(prefers-color-scheme: light)']),
        ]).pipe(
            takeUntil(this._unsubscribeAll),
            map(([config, mql]) => {
                const options = {
                    scheme: config.scheme,
                    theme: config.theme,
                };

                // If the scheme is set to 'auto'...
                if (config.scheme === 'auto') {
                    // Decide the scheme using the media query
                    options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
                }

                return options;
            }),
        ).subscribe((options) => {
            // Store the options
            this.scheme = options.scheme;
            this.theme = options.theme;

            // Update the scheme and theme
            this._updateScheme();
            this._updateTheme();
        });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to navigation events for loading bar, layout updates, and scroll
        this._router.events.pipe(
            takeUntil(this._unsubscribeAll),
        ).subscribe((event) => {
            if (event instanceof NavigationStart) {
                this._fuseLoadingService.show();
            }

            if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                this._fuseLoadingService.hide();

                if (event instanceof NavigationEnd) {
                    // Update the layout
                    this._updateLayout();

                    // Scroll to top of the page
                    this._scrollToTop();
                }
            }
        });



        // Set the app version
        this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

        // Set the OS name
        this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }

    /**
     * Scroll to top of page and active scroll containers on navigation
     */
    private _scrollToTop(): void {
        const resetScrolls = () => {
            window.scrollTo(0, 0);

            if (!this._document) {
                return;
            }

            this._document.documentElement.scrollTop = 0;
            this._document.body.scrollTop = 0;

            // Reset scroll on all ancestors of router-outlet
            const outlets = this._document.querySelectorAll('router-outlet');
            outlets.forEach((outlet: any) => {
                let parent: HTMLElement | null = outlet.parentElement;
                while (parent && parent !== this._document.body) {
                    if (parent.scrollTop > 0) {
                        parent.scrollTop = 0;
                    }
                    parent = parent.parentElement;
                }
            });

            // Reset layout content scroll areas (excluding navigation and quick-chat)
            const scrollAreas = this._document.querySelectorAll('.overflow-y-auto');
            scrollAreas.forEach((area: any) => {
                if (area.closest('layout') && !area.closest('fuse-vertical-navigation') && !area.closest('quick-chat')) {
                    if (area.scrollTop > 0) {
                        area.scrollTop = 0;
                    }
                }
            });
        };

        resetScrolls();
        requestAnimationFrame(resetScrolls);
        setTimeout(resetScrolls, 50);
    }
}
